import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChildren, QueryList, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Pagination } from '@core/models/pagination.model';
import { LiveChat } from '@core/models/live-chat.model';
import { LiveChatScriptDataService } from './services/live-chat-script-data.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { OwlDateTimeInputDirective } from '@danielmoncada/angular-datetime-picker';
import { TranslateService } from '@ngx-translate/core';
import Swal from "sweetalert2";
import { AppPermissionService } from '@core/services/app-permission.service';
import { AIChatbotDialogComponent } from './dialogs/ai-chatbot-dialog/ai-chatbot-dialog.component';
import { UploadHttpService } from '@core/services/upload-http.service';
import { Lightbox } from 'ngx-lightbox';

interface MemberGroup {
  id: number;
  code: string;
  name: string;
  color: string;
  status: number;
  default: number;
  vip: number;
  trial: number;
  image: string;
  remarks: string | null
}

@Component({
  selector: 'kt-live-chat-script',
  templateUrl: './live-chat-script.component.html',
  styleUrls: ['./live-chat-script.component.scss']
})
export class LiveChatScriptComponent implements OnInit, OnDestroy {

  @HostListener('window:beforeunload', ['$event'])
  confirmLeavingPageBeforeSaving($event) {

    if(this.checkupdate()){
      $event.preventDefault();
      $event.returnValue = true;
      return $event;
    }
  }

  @ViewChildren(OwlDateTimeInputDirective) datePicker: QueryList<OwlDateTimeInputDirective<any>>;

  form: FormGroup;
  formContent: FormGroup;
  messages$ = this.liveChatScriptDataService.messages$;

  loading = false;
  searchBtnLoading = false;
  clearBtnLoading = false;
  dataLength = 0;
  params = '';
  buttonLoading = true;
  initialValues: any;

  mode: any;

  liveChat: any;
  initLiveChat: any;
  availableLiveChat = [];
  isEmptyContent = true;

  liveChat$: Observable<LiveChat[]>;
  liveChatData = [];

  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  displayLocale: any;
  activeFormLocale: any; // Track currently active locale in form tab
  backupPageSize = 30;
  backupPage = 1;
  unsaveStatus = true;

  changedObjects: any;

  sortingConfig = {
    'id': 'desc'
  };

  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };

  dropdown = {
    statuses: this.dropdownHttpService.statuses,
    locales: [],
    platform: this.dropdownHttpService.liveChatPlatform,
    memberGroups: this.dropdownHttpService.groups,
    perPage: this.dropdownHttpService.perPage,
  };

  memberGroupDropdownList = [];
  memberGroupSelectedItems = [];
  pluginTypes: any[] = [];
  activeType = 1; // 1=Live Chat, 2=Mini Game

  miniGamePlatform = [];

  memberGroupDropdownSettings = {
    text: "Please Select",
    enableSearchFilter: true,
    singleSelection: false,
    classes: "dropdown",
    primaryKey: "id",
    labelKey: "name",
    noDataLabel: "",
    showCheckbox: false,
    autoPosition: false,
    position: "bottom"
  };

  iniRequired = true;

  // image upload properties
  isImageUploading = false;
  imagePreview: any;

  // permissions
  canViewLiveChatScriptList: boolean;
  canCreateLiveChatScript: boolean;
  canViewLiveChatScriptDetails: boolean;
  canEditLiveChatScript: boolean;
  canUpdateLiveChatScriptStatus: boolean;
  canUpdateAIChatbotStatus: boolean;

  canViewMiniGameList: boolean;
  canCreateMiniGame: boolean;
  canViewMiniGameDetails: boolean;
  canEditMiniGame: boolean;
  canUpdateMiniGameStatus: boolean;

  private subscription = new Subscription();
  private subscriptions = new Subscription();
  private refreshSubcription = new Subscription();
  private localesSub = new Subscription();

  constructor(
    private dropdownHttpService: DropdownHttpService,
    private loadingBar: LoadingBarService,
    private liveChatScriptDataService: LiveChatScriptDataService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private appPermissionService: AppPermissionService,
    private uploadService: UploadHttpService,
    private lightbox: Lightbox,
  ) { }

  async canDeactivate(): Promise<boolean | Observable<boolean>> {
    // provide component specific logic to decide if component can or can't be deactivated
    if (this.checkupdate()) {
      const result = await Swal.fire({
        title: '<div class="text-center">Unsaved Changes</div>',
        html: '<div class="text-center">Are you sure you want to discard the changes?</div>',
        showDenyButton: true,
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: true,
        reverseButtons: true,
        denyButtonText: this.translateService.instant('Cancel'),
        confirmButtonText: this.translateService.instant('Discard'),
        icon: 'warning',
        customClass: {
          denyButton: 'deny-button',
          confirmButton: 'confirm-button',
        }
      }).then(result => {
        if (result.isConfirmed) {
          return (true);
        } else if (result.isDenied) {
          return (false);
        }
      });

      return result;
    }
    return true;
  }

  ngOnInit() {
    this.subscription.add(this.liveChatScriptDataService.getPluginTypes().pipe(
      tap((types: any[]) => {
        this.pluginTypes = types;
        const allowed = this.visiblePluginTypes;
        this.activeType = allowed.length ? allowed[0].id : 1;
      })
    ).subscribe(() => {
      // After plugin type is set, load data and UI
      this.onTypeChanged(this.activeType, false);
    }));

    this.subscription.add(this.liveChatScriptDataService.getMiniGamePlatform().subscribe((res: any[]) => {
      this.miniGamePlatform = res;
    }));

    this.localesSub = this.dropdownHttpService.locales.pipe(tap(res => {
      this.dropdown.locales = res;
      this.formInit();
      this.onSubmit();

      this.initLiveChat = JSON.stringify({ ...this.formContent.value });
    })).subscribe();

    this.dropdown.memberGroups.subscribe((res) => {
      this.memberGroupDropdownList = res;
    });

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      // Live Chat permissions
      this.canViewLiveChatScriptList = appPermissions.view_live_chat_script_list;
      this.canCreateLiveChatScript = appPermissions.create_live_chat_script;
      this.canViewLiveChatScriptDetails = appPermissions.view_live_chat_script_details;
      this.canEditLiveChatScript = appPermissions.edit_live_chat_script;
      this.canUpdateLiveChatScriptStatus = appPermissions.update_live_chat_script_status;
      this.canUpdateAIChatbotStatus = appPermissions.update_live_chat_script_status; // Since is temporary, will share use this permissions

      // Mini Game permissions
      this.canViewMiniGameList = appPermissions.view_mini_game_list;
      this.canCreateMiniGame = appPermissions.mini_game_create_mini_game;
      this.canViewMiniGameDetails = appPermissions.view_mini_game_details;
      this.canEditMiniGame = appPermissions.mini_game_edit_mini_game;
      this.canUpdateMiniGameStatus = appPermissions.update_mini_game_status;

      // Revalidate plugin tab selection if current type not authorized
      if (this.activeType === 1 && !this.canViewLiveChatScriptList && this.canViewMiniGameList) {
        this.activeType = 2;
      } else if (this.activeType === 2 && !this.canViewMiniGameList && this.canViewLiveChatScriptList) {
        this.activeType = 1;
      }
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.localesSub.unsubscribe();
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
    this.refreshSubcription.unsubscribe();
  }

  onClear() {
    this.form.patchValue({
      status: 'all',
      settings_locale_id: 'all',
      live_chat_platform_id: 'all',
      mini_game_platform_id: 'all',
      type: this.activeType,
    })
  }

  onTypeChanged(type: number, doSearch = true) {
    // allow only accessible plugin type
    if ((type === 1 && !this.canViewLiveChatScriptList) || (type === 2 && !this.canViewMiniGameList)) {
      return;
    }

    if (this.activeType === type) {
      return;
    }

    this.activeType = type;

    // If forms are not ready yet, only set activeType and rely on ngOnInit formInit
    if (!this.form) {
      return;
    }

    // Reset form filters and content contexts when switching tab
    this.onClear();
    this.onClearContent();

    if (doSearch) {
      this.onSubmit();
    }

    // Adjust validation for page-specific fields
    if (this.formContent) {
      if (this.activeType === 1) {
        this.formContent.controls['live_chat_platform_id'].setValidators([Validators.required]);
        this.formContent.controls['mini_game_platform_id'].clearValidators();
        this.formContent.controls['member_group'].setValidators([Validators.required]);
      } else {
        this.formContent.controls['live_chat_platform_id'].clearValidators();
        this.formContent.controls['mini_game_platform_id'].setValidators([Validators.required]);
        this.formContent.controls['member_group'].clearValidators();
      }
      this.formContent.controls['live_chat_platform_id'].updateValueAndValidity();
      this.formContent.controls['mini_game_platform_id'].updateValueAndValidity();
      this.formContent.controls['member_group'].updateValueAndValidity();
    }
  }

  onClearContent() {
    this.formContent.patchValue({
      live_chat_platform_id: null,
      mini_game_platform_id: null,
      status: 1,
      member_group: null,
    })

    this.dropdown.locales.forEach((element: any) => {
      this.formContent.patchValue({
        locales: {
          [element.id]: {
            script: null,
            chat_page: null,
            image_path: null
          }
        }
      });
    });

    this.memberGroupSelectedItems = [];
    this.imagePreview = null;

    this.initLiveChat = JSON.stringify({ ...this.formContent.value });
  }

  reload(clearSearch?: boolean) {
    this.isEmptyContent = true;
    this.mode = '';
    this.onClearContent();
    this.onSubmit(clearSearch);
    this.buttonLoading = false;
  }

  async onSubmitClick(clearSearch?: boolean) {
    await this.onCheckUnsave();
    if (this.unsaveStatus == true) {

      this.isEmptyContent = true;
      this.mode = '';
      this.onClearContent();
      this.onSubmit(clearSearch);
    }
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;

    if (clearSearch) {
      this.onClear();
    }

    of(this.form.value).pipe(
      map(data => this.filterFormFields(data)),
      tap((data) => {
        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.loadingBar.start();
        return this.liveChat$ = this.liveChatScriptDataService.getWithQuery(`?${this.params}&perPage=${this.pageSize}&${this.generateSortingParam()}`).pipe(
          tap(res => {
            this.liveChatData = res;
            this.displayLocale = this.displayLocale ? this.displayLocale : res.length && this.form.value.settings_locale_id != 'all' ? this.dropdown.locales.filter(x => x.id == this.form.value.settings_locale_id)[0] : this.dropdown.locales[0];
            this.page = 1;
            this.pagination = this.liveChatScriptDataService.pagination;
            this.loadingBar.complete();
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.buttonLoading = false;
            this.dataLength = res.length;
            this.availableLiveChat = res;
            this.cdr.detectChanges();
          })
        );
      }),
    ).subscribe();
  }

  async onSave() {
    this.buttonLoading = true;

    // To set "Save" button to disable (To prevent call API in multiple times when double click)
    this.formContent.setErrors({ 'invalid': true });

    const data: any = {
      id: this.liveChat ? this.liveChat.id : null,
      type: this.activeType,
      ...this.formContent.value,
    };

    if (this.activeType === 1) {
      // livechat
      delete data.mini_game_platform_id;
    } else if (this.activeType === 2) {
      // minigame
      delete data.live_chat_platform_id;
      delete data.member_group;

      // explicitly remove chat_page when minigame mode since not required per spec
      const locales = data.locales || {};
      Object.keys(locales).forEach((localeKey) => {
        if (locales[localeKey]) {
          locales[localeKey].chat_page = null;
        }
      });
    }

    Object.keys(data).forEach((key) => {
      if (data[key] == null || data[key] === '' || data[key] === 'Invalid date') {
        delete data[key];
      }
    });

    const modifiedData = await this.checkDuplicateMemberGroup(data);

    if (!modifiedData) {
      // User cancelled
      this.buttonLoading = false;
      this.formContent.setErrors(null);
      return;
    }

    switch (this.mode) {
      case 'edit':
        this.subscription = this.liveChatScriptDataService.updateLiveChat(this.liveChat.id, modifiedData).pipe(
          tap((res: any) => {
            this.messages$.next(res.message);
            this.buttonLoading = false;
          }),
          catchError((error) => {
            this.buttonLoading = false;
            this.form.setErrors(null);
            this.form.enable();
            throw error;
          })
        ).subscribe();
        break;
      case 'create':
        this.subscription = this.liveChatScriptDataService.addLiveChat(modifiedData).pipe(
          tap((res: any) => {
            this.messages$.next(res.message);
            this.buttonLoading = false;
          }),
          catchError((error) => {
            this.buttonLoading = false;
            this.formContent.setErrors(null);
            this.formContent.enable();
            throw error;
          })
        ).subscribe();
        break;
    }
  }

  checkDuplicateMemberGroup(data: any): Promise<any> {
    return new Promise((resolve) => {
      if (this.activeType === 2) {
        // Mini game does not require member groups check
        return resolve(data);
      }

      const existingGroups = data.member_group;
      const currentId = data.id;
      const conflictMap: { [groupId: number]: number[] } = {}; // { groupId: [chatIds] }

      if (!existingGroups || !Array.isArray(existingGroups)) {
        return resolve(data);
      }

      for (const chat of this.availableLiveChat) {
        if (chat.id == currentId) continue;

        for (const groupId of chat.member_group_ids) {
          if (existingGroups.some(existing => existing == groupId)) {
            if (!conflictMap[groupId]) {
              conflictMap[groupId] = [];
            }
            conflictMap[groupId].push(chat.id);
          }
        }
      }

      const conflictingGroupIds = Object.keys(conflictMap).map(id => Number(id));

      if (conflictingGroupIds.length > 0) {
        const conflictDetails = conflictingGroupIds.map(groupId => {
          const groupName = this.memberGroupDropdownList.find(item => item.id == groupId)?.name || `ID: ${groupId}`;
          const chatIds = conflictMap[groupId].join(', ');
          return `${groupName} (Live Chat ID: ${chatIds})`;
        });
  
        const groupList = conflictDetails.join('<br>');

        Swal.fire({
          title: 'Conflict Member Group',
          html: `The following member groups are already assigned to other live chats: <br>${groupList}<br><br>If you proceed, they will be removed from the existing live chats and reassigned to this one. Do you want to continue?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes',
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed) {
            resolve({ ...data, replace_others: true });
          } else {
            resolve(null); // Cancelled
          }
        });
      } else {
        resolve(data); // No conflict, return original
      }
    });
  }

  changeLocale(locale: any) {
    this.displayLocale = locale;
  }

  clearContent(locale: any) {
    Swal.fire({
      title: 'Delete Content',
      text: 'Are you sure you want to delete contents for ' + locale.code + '?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((response) => {
      if (response.isConfirmed) {
        this.formContent.patchValue({
          locales: {
            [locale.id]: {
              script: null,
              chat_page: null,
            }
          }
        });

        let locales = this.formContent.value.locales;
        this.isEmptyContent = true;

        let itemControls = <FormGroup>this.formContent.controls['locales'];
        Object.keys(locales).forEach((sf) => {
          if (locales[sf]['script'] != null && locales[sf]['chat_page'] != null) {
            this.isEmptyContent = false;
          }

          let itemFormGroup = <FormGroup>itemControls.controls[sf];
          itemFormGroup.controls["chat_page"].clearValidators();
          itemFormGroup.controls["chat_page"].updateValueAndValidity();
          itemFormGroup.controls["script"].clearValidators();
          itemFormGroup.controls["script"].updateValueAndValidity();

        });
      }
      this.cdr.detectChanges();
    });

  }

  onChangeStatus($event, row: any) {
    const data = {
      id: row.id,
      status: $event.target.checked ? 1 : 0,
      type: this.activeType
    };

    this.subscription = this.liveChatScriptDataService.updateStatus(data).pipe(
      tap((res: any) => {
        this.messages$.next(res.message);
        this.buttonLoading = false;
      }),
      catchError((error) => {
        this.buttonLoading = false;
        const ele = document.getElementById(row.id) as HTMLInputElement;
        ele.checked = false;
        throw error;
      })
    ).subscribe();
  }

  async onPerPage(size: Event) {
    await this.onCheckUnsave();
    if (this.unsaveStatus == true) {
      this.page = 1;
      this.pageSize = +(size.target as HTMLSelectElement).value;
      this.backupPageSize = this.pageSize;
      this.onViewPageBy(this.page, this.pageSize, this.params);
    }
    else {
      this.pageSize = this.backupPageSize;
    }
  }

  async onViewPageBy(page = 1, pageSize?: number, params?: string) {
    await this.onCheckUnsave();
    if (this.unsaveStatus == true) {
      this.backupPage = page;
      pageSize = this.pageSize;
      params = this.params ? `&${this.params}` : '';
      this.loading = true;
      this.loadingBar.start();
      return this.liveChat$ = this.liveChatScriptDataService.getWithQuery(`?${this.params}&page=${page}&perPage=${this.pageSize}&${this.generateSortingParam()}`).pipe(
        tap(res => {
          this.liveChatData = res;
          this.pagination = this.liveChatScriptDataService.pagination;
          this.loading = false;
          this.dataLength = res.length;
          this.availableLiveChat = res;
          this.loadingBar.complete();
        })
      );
    }
    else {
      this.page = this.backupPage;
    }
  }

  expandRowCreate(mode: any, close?: boolean) {
    let liveChatInput = JSON.stringify({ ...this.formContent.value });
    this.changedObjects = this.initLiveChat != liveChatInput ? true : false;

    if (close != true && this.changedObjects == false) {
      this.liveChat = null;
      this.changedObjects = false;
      this.initLiveChat = null;

      this.mode = mode;
      this.activeFormLocale = this.dropdown.locales[0]; // Initialize to first locale
      this.cdr.detectChanges();
      this.onClearContent();
    } else {
      if (this.changedObjects == true) {
        Swal.fire({
          title: '<div class="text-center">Unsaved Changes</div>',
          html: '<div class="text-center">Are you sure you want to discard the changes?</div>',
          showDenyButton: true,
          showCloseButton: true,
          showCancelButton: false,
          showConfirmButton: true,
          reverseButtons: true,
          denyButtonText: this.translateService.instant('Cancel'),
          confirmButtonText: this.translateService.instant('Discard'),
          icon: 'warning',
          customClass: {
            denyButton: 'deny-button',
            confirmButton: 'confirm-button',
          }
        }).then(result => {
          if (result.isConfirmed) {

            this.isEmptyContent = true;
            this.mode = '';
            this.unsaveStatus = true;
            this.onClearContent();

            // Check if reopen needed
            this.checkReopen(mode);
          }
        });
      }
      else {

        this.isEmptyContent = true;
        this.mode = '';
        this.unsaveStatus = true;
        this.onClearContent();
      }
    }

    this.cdr.detectChanges();
  }

  expandRowEdit(mode: any, row?: any, close?: boolean) {
    if (close != true) close = this.liveChat == row ? true : false;
    let liveChatInput = JSON.stringify({ ...this.formContent.value });
    this.changedObjects = this.initLiveChat && this.initLiveChat != liveChatInput ? true : false;

    if (close != true && this.changedObjects == false) {
      this.liveChat = row;
      this.changedObjects = false;
      this.initLiveChat = null;

      this.mode = mode;

      this.cdr.detectChanges();

      if (this.activeType === 1) {
        this.formContent.patchValue({
          live_chat_platform_id: this.liveChat.live_chat_platform_id,
          status: this.liveChat.status,
          member_group: this.liveChat.member_group_ids,
        });

        const memberGroupIds = this.liveChat.member_group_ids ? this.liveChat.member_group_ids.map(id => Number(id)) : [];
        this.memberGroupSelectedItems = this.memberGroupDropdownList.filter((item: MemberGroup) => memberGroupIds.includes(item.id));
      } else {
        this.formContent.patchValue({
          mini_game_platform_id: this.liveChat.mini_game_platform_id,
          status: this.liveChat.status,
          member_group: null,
        });

        this.memberGroupSelectedItems = [];
      }

      // reset all locales to null
      this.dropdown.locales.forEach((element: any) => {
        this.formContent.patchValue({
          locales: {
            [element.id]: {
              script: null,
              chat_page: null,
              image_path: null
            }
          }
        });
      });

      this.liveChat.locales.forEach((element: any) => {
        this.formContent.patchValue({
          locales: {
            [element.settings_locale_id]: {
              script: element.script,
              chat_page: element.chat_page,
              image_path: element.image_path
            }
          }
        });
      });

      this.activeFormLocale = this.dropdown.locales[0]; // Initialize to first locale
      this.initLiveChat = JSON.stringify({ ...this.formContent.value });
    } else {
      this.changedObjects = this.initLiveChat != JSON.stringify({ ...this.formContent.value }) ? true : false;
      if (this.changedObjects == true) {
        Swal.fire({
          title: '<div class="text-center">Unsaved Changes</div>',
          html: '<div class="text-center">Are you sure you want to discard the changes?</div>',
          showDenyButton: true,
          showCloseButton: true,
          showCancelButton: false,
          showConfirmButton: true,
          reverseButtons: true,
          denyButtonText: this.translateService.instant('Cancel'),
          confirmButtonText: this.translateService.instant('Discard'),
          icon: 'warning',
          customClass: {
            denyButton: 'deny-button',
            confirmButton: 'confirm-button',
          }
        }).then(result => {
          if (result.isConfirmed) {

            this.isEmptyContent = true;
            this.mode = '';
            this.unsaveStatus = true;
            this.onClearContent();

            // Check if reopen needed
            if (this.liveChat == row) {
              this.liveChat = null;
            }
            else {
              this.liveChat = null;
              this.checkReopen(mode, row);
            }
          }
        });
      }
      else {

        this.isEmptyContent = true;
        this.mode = '';
        this.unsaveStatus = true;
        this.liveChat = null;
        this.onClearContent();
      }
    }
  }

  checkReopen(mode: any, row?: any) {
    switch (mode) {
      case 'edit':
        this.expandRowEdit(mode, row)
        break;
      case 'create':
        this.expandRowCreate(mode);
        break;
    }
  }

  localeIndex() {
    const index = this.dropdown.locales.findIndex(item => {
      return item.id == this.displayLocale['id'];
    });

    return index;
  }

  private filterFormFields(formData: any) {
    const fields: any = {};

    const status = formData.status;
    if (status && status !== 'all') {
      fields.status = status === true ? 1 : status;
    }

    const locale = formData.settings_locale_id;
    if (locale && locale !== 'all') {
      fields.settings_locale_id = locale;
    }

    fields.type = this.activeType;

    if (this.activeType === 1) {
      const platformId = formData.live_chat_platform_id;
      if (platformId && platformId !== 'all') {
        fields.live_chat_platform_id = platformId;
      }
    } else if (this.activeType === 2) {
      const platformId = formData.mini_game_platform_id;
      if (platformId && platformId !== 'all') {
        fields.mini_game_platform_id = platformId;
      }
    }

    return fields;
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  async onSortColumn(property: string) {
    await this.onCheckUnsave();
    if (this.unsaveStatus == true) {
      // Reset other columns
      for (const key in this.sortingConfig) {
        if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
          if (key == property) {
            this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
          } else {
            this.sortingConfig[key] = 'desc';
          }
        }
      }

      // User selection
      this.sortingSelection.sort_by = property;

      if (this.sortingSelection.sort_by === property) {
        // Same column
        this.sortingSelection.sort_order = this.sortingConfig[property];
      } else {
        // Switch to other column
        this.sortingConfig[property] = 'asc';
        this.sortingSelection.sort_order = 'asc';
      }
      // Load Data
      this.onViewPageBy(this.page, this.pageSize, this.params);
    }
  }

  private formInit() {
    const buildContents = () => {
      let fields = {};
      this.dropdown.locales.forEach((element: any) => {
        const subFields = new FormGroup({
          settings_locale_id: new FormControl(element.id),
          script: new FormControl(null),
          chat_page: new FormControl(null),
          image_path: new FormControl(null) // For minigame image upload per locale
        });
        fields = { ...fields, [element.id]: subFields };
      });
      return fields;
    };

    this.form = new FormGroup({
      // Filter
      status: new FormControl('all'),
      settings_locale_id: new FormControl('all'),
      live_chat_platform_id: new FormControl('all'),
      mini_game_platform_id: new FormControl('all'),
      type: new FormControl(this.activeType),
    });

    this.formContent = new FormGroup({
      // Create dialog
      live_chat_platform_id: new FormControl(null, [Validators.required]),
      mini_game_platform_id: new FormControl(null),
      status: new FormControl(null, [Validators.required]),
      locales: new FormGroup(buildContents()),
      member_group: new FormControl(null, [Validators.required]),
    });

    this.formContent.valueChanges.subscribe(data => {
      let isContent = false;
      Object.keys(data['locales']).forEach((sf) => {
        if (this.activeType === 2) {
          if (data['locales'][sf]['script'] != null && data['locales'][sf]['script'] != '') {
            isContent = true;
          }
        } else {
          if (data['locales'][sf]['script'] != null && data['locales'][sf]['script'] != '' && data['locales'][sf]['chat_page'] != null && data['locales'][sf]['chat_page'] != '') {
            isContent = true;
          }
        }
      })
      this.isEmptyContent = isContent ? false : true;
    })

    this.formContent.markAllAsTouched();
    this.formContent.markAsDirty();
  }

  checkupdate() {
    return this.initLiveChat !== JSON.stringify({ ...this.formContent.value }) ? true : false;
  }

  displayLocaleContent(row: any, type: any) {
    let liveChat = this.availableLiveChat.filter(x => x.id == row.id)[0]['locales'];
    liveChat = liveChat.filter(x => x['locale_name'] == this.displayLocale['code']);

    if (liveChat.length > 0 && liveChat[0][type] !== null) {
      if (type == 'script') {
        return liveChat[0]['script'];
      } else {
        return liveChat[0]['chat_page'];
      }
    }
    else {
      if (type == 'script') {
        return '-';
      } else {
        return '-';
      }
    }
  }

  checkContent(locale: any) {

    let script = this.formContent.value.locales[locale.id].script;
    let chat_page = this.formContent.value.locales[locale.id].chat_page;

    if (this.activeType === 2) {
      return !(script == null || script == '');
    }

    // Live chat requires both script and chat page
    if (script == null || script == '' || chat_page == null || chat_page == '') {
      return false;
    }
    else {
      return true;
    }
  }

  get visiblePluginTypes() {
    if (!Array.isArray(this.pluginTypes)) {
      return [];
    }

    return this.pluginTypes.filter(type => {
      if (type.id === 1) {
        return this.canViewLiveChatScriptList ?? false;
      }
      if (type.id === 2) {
        return this.canViewMiniGameList ?? false;
      }
      return true;
    });
  }

  get canCreateScript() {
    return this.activeType === 1 ? this.canCreateLiveChatScript : this.canCreateMiniGame;
  }

  get canViewScriptDetails() {
    return this.activeType === 1 ? this.canViewLiveChatScriptDetails : this.canViewMiniGameDetails;
  }

  get canEditScript() {
    return this.activeType === 1 ? this.canEditLiveChatScript : this.canEditMiniGame;
  }

  get canUpdateScriptStatus() {
    return this.activeType === 1 ? this.canUpdateLiveChatScriptStatus : this.canUpdateMiniGameStatus;
  }

  onChangeValidate(event: any , key, field: string){
    let content_type = event.target.value;
    let itemControls = <FormGroup>this.formContent.controls['locales'];
    let itemFormGroup = <FormGroup>itemControls.controls[key];

    if (this.activeType === 2) {
      // Mini-game: only script is required, no chat page requirements
      return;
    }

    if (field == 'script') {
      if (content_type.length != 0) {
        /// add validators if the script is not null
        itemFormGroup.controls["chat_page"].setValidators(Validators.required);
        itemFormGroup.controls["chat_page"].updateValueAndValidity();

      } else {
        itemFormGroup.controls["chat_page"].clearValidators();
        itemFormGroup.controls["chat_page"].updateValueAndValidity();
      }
    } else if (field == 'chat_page') {
      if (content_type.length != 0) {
        /// add validators if the chat_page is not null
        itemFormGroup.controls["script"].setValidators(Validators.required);
        itemFormGroup.controls["script"].updateValueAndValidity();

      } else {
        itemFormGroup.controls["script"].clearValidators();
        itemFormGroup.controls["script"].updateValueAndValidity();
      }
    }
  }

  // Image upload methods
  onUploadFile(event: any) {
    this.isImageUploading = true;

    const file: File = event.target.files[0];
    const formData = new FormData();

    if (file !== undefined) {
      formData.append('files', file, file.name);
      formData.append('type', 'uploads');

      this.uploadService.upload(formData).subscribe(
        res => {
          this.imagePreview = res;
          const localeId = this.activeFormLocale ? this.activeFormLocale.id : this.dropdown.locales[0].id;
          this.formContent.patchValue({
            locales: {
              [localeId]: {
                image_path: res[0]
              }
            }
          });
          this.isImageUploading = false;
          this.cdr.detectChanges();
        },
        err => {
          this.isImageUploading = false;
          this.cdr.detectChanges();
        }
      );
    } else {
      this.isImageUploading = false;
      this.cdr.detectChanges();
    }
  }

  onRemoveFile() {
    const localeId = this.activeFormLocale ? this.activeFormLocale.id : this.dropdown.locales[0].id;
    this.formContent.patchValue({
      locales: {
        [localeId]: {
          image_path: null
        }
      }
    });
    this.imagePreview = null;
  }

  private onCheckUnsave() {
    return new Promise<void>((resolve, reject) => {
      let liveChatInput = JSON.stringify({ ...this.formContent.value });
      this.changedObjects = this.initLiveChat && this.initLiveChat != liveChatInput ? true : false;

      if (this.changedObjects == true) {
        Swal.fire({
          title: '<div class="text-center">Unsaved Changes</div>',
          html: '<div class="text-center">Are you sure you want to discard the changes?</div>',
          showDenyButton: true,
          showCloseButton: true,
          showCancelButton: false,
          showConfirmButton: true,
          reverseButtons: true,
          denyButtonText: this.translateService.instant('Cancel'),
          confirmButtonText: this.translateService.instant('Discard'),
          icon: 'warning',
          customClass: {
            denyButton: 'deny-button',
            confirmButton: 'confirm-button',
          }
        }).then(result => {
          if (result.isConfirmed) {

            this.isEmptyContent = true;
            this.mode = '';
            this.liveChat = null;
            this.unsaveStatus = true;
            this.onClearContent();
            resolve();
          }
          else {
            this.unsaveStatus = false;
            resolve();
          }
        });
      }
      else {
        resolve();
      }
    });
  }

  onOpenLivechatSettings():void {
    this.dialog.open(AIChatbotDialogComponent, {
      width: '800px'
    });
  }
}
